import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Calendar, MapPin } from 'lucide-react';
import { PortfolioConfig, Achievement } from '../types/config';

interface ExperienceProps {
  config: PortfolioConfig;
}

const Experience: React.FC<ExperienceProps> = ({ config }) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setHeight(rect.height);
    }
  }, [ref]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 50%"],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <section id="experience" className="section-padding from-slate-50 via-blue-50 to-indigo-50 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 dark:opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(99, 102, 241, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 50% 25%, rgba(59, 130, 246, 0.2) 0%, transparent 50%)`,
          backgroundSize: '400px 400px, 300px 300px, 500px 500px',
          backgroundPosition: '0 0, 200px 200px, 100px 100px'
        }}></div>
      </div>

      {/* Floating geometric shapes */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-primary-400/20 to-purple-400/20 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute top-40 right-20 w-16 h-16 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
      <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-pulse" style={{animationDelay: '2s'}}></div>

      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Work <span className="gradient-text">Experience</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-dark-600 dark:text-dark-300 max-w-2xl mx-auto">
            My professional journey through innovative companies and impactful projects
          </p>
        </motion.div>

        <div 
          className="w-full font-sans md:px-10"
          ref={containerRef}
        >
          <div ref={ref} className="relative max-w-7xl mx-auto pb-20">
            {config.experience.map((exp, index) => (
              <div
                key={`${exp.company}-${exp.duration}`}
                className="flex justify-start pt-10 md:pt-40 md:gap-10"
              >
                <div className="sticky flex flex-col md:flex-row z-40 items-center top-40 self-start max-w-xs lg:max-w-sm md:w-full">
                  <div className="h-10 absolute left-3 md:left-3 w-10 rounded-full bg-white/90 dark:bg-dark-900/90 backdrop-blur-sm flex items-center justify-center shadow-lg border border-white/20 dark:border-dark-700/20">
                    <div className="h-4 w-4 rounded-full bg-gradient-to-r from-primary-500 to-purple-500 border-2 border-white dark:border-dark-800 shadow-sm" />
                  </div>
                  <h3 className="hidden md:block text-xl md:pl-20 md:text-4xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
                    {exp.duration}
                  </h3>
                </div>

                <div className="relative pl-16 pr-3 md:pl-4 md:pr-4 w-full min-w-0">
                  <h3 className="md:hidden block text-lg sm:text-xl mb-4 text-left font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
                    {exp.duration}
                  </h3>
                  <GlowCard exp={exp} index={index} />
                </div>
              </div>
            ))}
            <div
              style={{
                height: height + "px",
              }}
              className="absolute md:left-8 left-8 top-0 overflow-hidden w-[2px] bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))] from-transparent from-[0%] via-primary-200 dark:via-primary-700 to-transparent to-[99%] [mask-image:linear-gradient(to_bottom,transparent_0%,black_10%,black_90%,transparent_100%)]"
            >
              <motion.div
                style={{
                  height: heightTransform,
                  opacity: opacityTransform,
                }}
                className="absolute inset-x-0 top-0 w-[2px] bg-gradient-to-t from-primary-500 via-purple-500 to-transparent from-[0%] via-[10%] rounded-full"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Glow Card Component
interface GlowCardProps {
  exp: any;
  index: number;
}

const GlowCard: React.FC<GlowCardProps> = ({ exp, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="group relative">
      {/* Glow Effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-primary-600 via-purple-600 to-cyan-600 rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-500"></div>
      
      {/* 3D Card */}
      <motion.div 
        className="relative bg-white/90 dark:bg-dark-900/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 dark:border-dark-700/20 transform transition-all duration-500 group-hover:shadow-3xl group-hover:-translate-y-1 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        animate={{
          height: isExpanded ? "auto" : "auto",
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {/* Compact View (Default) */}
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start flex-1 min-w-0">
              {/* Company Logo */}
              {exp.logo && (
                <div className="w-11 h-11 sm:w-12 sm:h-12 mr-3 sm:mr-4 flex-shrink-0">
                  <img
                    src={exp.logo}
                    alt={`${exp.company} logo`}
                    className="w-full h-full object-contain rounded-lg bg-white dark:bg-dark-800 p-1 shadow-sm border border-gray-200 dark:border-gray-700"
                    onError={(e) => {
                      // Fallback to text if image fails to load
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-dark-800 dark:text-white mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300 break-words">
                  {exp.position}
                </h3>
                <p className="text-base sm:text-lg font-semibold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent mb-2 break-words">
                  {exp.company}
                </p>
                {/* Location travels with the content so it never competes for width on small screens */}
                <div className="flex items-center text-sm text-dark-500 dark:text-dark-400 mb-2">
                  <MapPin className="w-4 h-4 mr-1.5 text-primary-500 flex-shrink-0" />
                  <span className="break-words">{exp.location}</span>
                </div>
                <p className="text-sm text-dark-600 dark:text-dark-300 leading-relaxed break-words">
                  {exp.description}
                </p>
              </div>
            </div>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="flex-shrink-0 text-primary-500"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </motion.div>
          </div>
        </div>

        {/* Expanded Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-700">
                {/* Description */}
                {/* <div className="pt-6 mb-6">
                  <p className="text-dark-600 dark:text-dark-300 leading-relaxed text-base">
                    {exp.description}
                  </p>
                </div> */}

                {/* Achievements */}
                <div className="pt-6 mb-6">
                  <h4 className="font-semibold text-dark-800 dark:text-white mb-3 text-base">
                    Key Achievements:
                  </h4>
                  <ul className="space-y-2">
                    {exp.achievements.map((achievement: Achievement, idx: number) => {
                      if (typeof achievement === 'string') {
                        return (
                          <li key={idx} className="flex items-start text-sm text-dark-600 dark:text-dark-300">
                            <span className="w-2 h-2 bg-gradient-to-r from-primary-500 to-purple-500 rounded-full mt-2 mr-3 flex-shrink-0 shadow-sm"></span>
                            {achievement}
                          </li>
                        );
                      }
                      const [text, subItems] = Object.entries(achievement)[0];
                      return (
                        <li key={idx}>
                          <div className="flex items-start text-sm text-dark-600 dark:text-dark-300">
                            <span className="w-2 h-2 bg-gradient-to-r from-primary-500 to-purple-500 rounded-full mt-2 mr-3 flex-shrink-0 shadow-sm"></span>
                            {text}
                          </div>
                          <ul className="ml-7 mt-1 space-y-1">
                            {subItems.map((sub: string, subIdx: number) => (
                              <li key={subIdx} className="flex items-start text-sm text-dark-600 dark:text-dark-300">
                                <span className="w-1.5 h-1.5 bg-gradient-to-r from-primary-400 to-purple-400 rounded-full mt-2 mr-2.5 flex-shrink-0 opacity-70"></span>
                                {sub}
                              </li>
                            ))}
                          </ul>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Technologies */}
                <div>
                  <h4 className="font-semibold text-dark-800 dark:text-white mb-3 text-base">
                    Technologies:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {exp.technologies.map((tech: string, idx: number) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-gradient-to-r from-primary-100 to-purple-100 dark:from-primary-900/50 dark:to-purple-900/50 text-primary-700 dark:text-primary-300 text-xs font-medium rounded-full border border-primary-200 dark:border-primary-800/50 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Experience; 